import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { ROUTE } from '~/app/routes';
import { Details } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardAccountRepository } from '~/repositories/CardAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';

import { CardAccountProps } from './CardAccount';

interface Params {
  id: string;
}

const cardAccountQueryKey = (id: string) => ['card-account', id] as const;

export const PageCardAccountApiResponse: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { cardAccount: outletCardAccount } = useOutletContext<CardAccountProps>();

  const { data: cardAccount, error: cardAccountError } = useQuery({
    queryKey: cardAccountQueryKey(id!),
    queryFn: () => CardAccountRepository.get(id!),
    enabled: !!id && !outletCardAccount,
    initialData: outletCardAccount,
  });

  useEffect(() => {
    if (cardAccountError) {
      navigate(ROUTE.CARD_ACCOUNTS);
      addDangerNotification({
        content: (cardAccountError as any).message || 'Failed to load card account',
      });
    }
  }, [cardAccountError, navigate, addDangerNotification]);

  const formattedCardAccount = useMemo(
    () => (cardAccount ? (cardAccount as unknown as Record<string, unknown>) : undefined),
    [cardAccount]
  );

  if (!cardAccount) {
    return null;
  }

  return (
    <Inner pt={24} px={0} pb={0}>
      <Details
        label="API Response"
        formattedData={formattedCardAccount}
        rawData={cardAccount as unknown as Record<string, unknown> | undefined}
      />
    </Inner>
  );
};
